/**
 * Builds query parameters for new message URLs with recipient information
 * @param accountId - Optional account ID for pre-filling recipient
 * @param name - Optional name for pre-filling recipient
 * @returns URLSearchParams object with the provided parameters
 */
export function buildNewMessageParams(
  accountId?: string | null,
  name?: string | null,
): URLSearchParams {
  const params = new URLSearchParams();

  if (accountId) {
    params.append('accountId', accountId);
  }
  if (name) {
    params.append('name', name);
  }

  return params;
}

/**
 * Builds a new message URL with optional recipient query parameters
 * @param baseUrl - Base URL (e.g., '/messages/new' or a full URL)
 * @param accountId - Optional account ID for pre-filling recipient
 * @param name - Optional name for pre-filling recipient
 * @param useAmpersand - If true, uses '&' instead of '?' for query params (when base URL already has params)
 * @returns Complete URL string with query parameters
 */
export function buildNewMessageUrl(
  baseUrl: string,
  accountId?: string | null,
  name?: string | null,
  useAmpersand = false,
): string {
  const params = buildNewMessageParams(accountId, name);
  const queryString = params.toString();

  if (!queryString) {
    return baseUrl;
  }

  const separator = useAmpersand ? '&' : '?';
  return `${baseUrl}${separator}${queryString}`;
}

/**
 * Navigates to a new message URL by replacing the current location
 * @param baseUrl - Base URL (e.g., '/messages/new' or a full URL)
 * @param accountId - Optional account ID for pre-filling recipient
 * @param name - Optional name for pre-filling recipient
 * @param useAmpersand - If true, uses '&' instead of '?' for query params
 */
export function navigateToNewMessage(
  baseUrl: string,
  accountId?: string | null,
  name?: string | null,
  useAmpersand = false,
): void {
  const url = buildNewMessageUrl(baseUrl, accountId, name, useAmpersand);
  window.location.href = url;
}

